import { useCallback, useState } from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { fg } from '@confluence/feature-gating';

import { BACKEND_MAX_RESULTS_PER_REQUEST } from './useParsedMacroParams.ts';
import type {
	useContentByLabelDataQuery$data,
	useContentByLabelDataQuery$variables,
} from './__generated__/useContentByLabelDataQuery.graphql.ts';

type DeepMutable<T> = {
	-readonly [P in keyof T]: DeepMutable<T[P]>;
};

interface UseContentByLabelDataResult {
	data: useContentByLabelDataQuery$data | undefined;
	error: Error | null;
	loading: boolean;
	hasNextPage: boolean;
	isLoadingMore: boolean;
	loadMore: (() => Promise<void>) | undefined;
	loadMoreError: Error | undefined;
}

/**
 * Hook that fetches content matching the given CQL query via the
 * confluence_search GraphQL endpoint.
 *
 * Accepts pre-built query variables from useParsedMacroParams (via
 * macroParamsForQuery), avoiding any reprocessing of macro params here.
 *
 * `maxResults` is the total number of results the macro is configured to show.
 * The backend returns at most `BACKEND_MAX_RESULTS_PER_REQUEST` per response, so
 * when pagination is enabled `loadMore` appends the next page until `maxResults`
 * is reached. Without it, any `maxResults` above that limit is silently
 * truncated to a single page.
 */
export const useContentByLabelData = (
	macroParamsForQuery: useContentByLabelDataQuery$variables,
	maxResults: number,
): UseContentByLabelDataResult => {
	const { cql } = macroParamsForQuery;
	const shouldSkipQuery = !cql?.trim();

	const [loadMoreError, setLoadMoreError] = useState<Error | undefined>();
	const [isLoadingMore, setIsLoadingMore] = useState(false);

	const { data, error, loading, fetchMore } = useQueryInterop<
		useContentByLabelDataQuery$data,
		useContentByLabelDataQuery$variables
	>(
		graphql`
			query useContentByLabelDataQuery(
				$cql: String!
				$after: String
				$first: Int
				$includeExcerpt: Boolean!
				$fallbackToBody: Boolean!
				$maxExcerptBlocks: Int
				$skipSearchTitle: Boolean = false
				$skipContentTitle: Boolean = true
			) {
				search: confluence_search(cql: $cql, first: $first, after: $after) {
					pageInfo {
						endCursor
						hasNextPage
					}
					nodes {
						title @skip(if: $skipSearchTitle)
						url
						lastModified
						content {
							id
							title @skip(if: $skipContentTitle)
							type
							subType
							macroExcerpt @include(if: $includeExcerpt) {
								atlasDocFormat(fallbackToBody: $fallbackToBody, maxBlocks: $maxExcerptBlocks)
							}
							body @include(if: $includeExcerpt) {
								atlas_doc_format {
									mediaToken {
										token
									}
								}
							}
							space {
								id
								key
								name
							}
							labels {
								nodes {
									id
									name
								}
							}
						}
					}
				}
			}
		`,
		{
			variables: {
				...macroParamsForQuery,
				skipSearchTitle: fg('cc_rewrite_macros_use_content_not_search_title'),
				skipContentTitle: !fg('cc_rewrite_macros_use_content_not_search_title'),
			},
			skip: shouldSkipQuery,
			fetchPolicy: 'cache-first',
		},
	);

	const loadedCount = data?.search?.nodes?.length ?? 0;
	const remainingCount = Math.max(maxResults - loadedCount, 0);
	const pageInfo = data?.search?.pageInfo;
	const canLoadMore =
		!shouldSkipQuery &&
		remainingCount > 0 &&
		Boolean(pageInfo?.hasNextPage) &&
		Boolean(pageInfo?.endCursor) &&
		fg('cc_rewrite_contentbylabel_paginate_results');

	const loadMore = useCallback(async (): Promise<void> => {
		const endCursor = pageInfo?.endCursor;
		if (!canLoadMore || !endCursor || loading || isLoadingMore) {
			return;
		}

		setLoadMoreError(undefined);
		setIsLoadingMore(true);

		await fetchMore({
			variables: {
				after: endCursor,
				first: Math.min(BACKEND_MAX_RESULTS_PER_REQUEST, remainingCount),
			},
			updateQuery: (
				prev: DeepMutable<useContentByLabelDataQuery$data>,
				{ fetchMoreResult }: { fetchMoreResult?: useContentByLabelDataQuery$data },
			) => {
				if (!fetchMoreResult?.search) {
					return prev;
				}

				return {
					...prev,
					search: {
						...(prev.search ?? {}),
						nodes: [...(prev.search?.nodes ?? []), ...(fetchMoreResult.search.nodes ?? [])],
						pageInfo: fetchMoreResult.search.pageInfo,
					},
				};
			},
		})
			.catch((err: unknown) => {
				setLoadMoreError(err instanceof Error ? err : new Error(String(err)));
			})
			.finally(() => {
				setIsLoadingMore(false);
			});
	}, [canLoadMore, fetchMore, isLoadingMore, loading, pageInfo?.endCursor, remainingCount]);

	return {
		data,
		error: error ?? null,
		loading: Boolean(loading),
		hasNextPage: canLoadMore,
		isLoadingMore,
		loadMore: canLoadMore ? loadMore : undefined,
		loadMoreError,
	};
};
